import { useTranslation } from "react-i18next";
import { ArrowDownWideNarrow, ArrowUpWideNarrow, ChevronDown, GripVertical, X } from "lucide-react";
import { SelectField } from "../../../components/ui";
import { renderFieldIcon } from "../../../fields/FieldList";
import { SortType } from "./SortBy";

const sortByOptions = [
  { value: "ASC", label: "Ascending" },
  { value: "DESC", label: "Descending" },
];

type SortByBuilderItemProps = {
  idx: number;
  availableSortFields: any[];
  sortBy: SortType;
  onChange: (sortBy: SortType) => void;
  onRemove: (idx: number) => void;
};

const SortByBuilderItem = ({
  idx,
  availableSortFields,
  sortBy,
  onChange,
  onRemove,
}: SortByBuilderItemProps) => {
  const { t } = useTranslation();

  return (
    <div className="w-full flex items-center justify-between space-x-2 pb-2">
      <div className="flex items-center gap-2">
        <div className="drag-handle cursor-move">
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="no-drag">
          <SelectField
            className="w-50"
            autoFocus={!sortBy.field}
            openMenuOnFocus={!sortBy.field}
            value={availableSortFields.find((item) => item.value === sortBy.field)}
            onChange={(value) => {
              onChange({
                field: value?.value || "",
                direction: "ASC",
              });
            }}
            options={availableSortFields}
            placeholder={t("Field")}
            dropdownIndicator={<ChevronDown className="w-3 h-3 text-gray-500" />}
            formatOptionLabel={(option) => {
              return (
                <div className={"w-full flex items-center space-x-2"} key={option.value}>
                  <div>{renderFieldIcon(option.data || {})}</div>
                  <div>{t(option.label)}</div>
                </div>
              );
            }}
            stylesOverride={{
              control: (base) => ({
                ...base,
                minHeight: 30,
                height: 30,
                borderRadius: 8,
                borderColor: "#d1d5dc",
                boxShadow: "none",
                ":hover": {
                  borderColor: "#d1d5db",
                },
                fontSize: 12,
              }),
              menu: (base) => ({
                ...base,
                width: "max-content",
                minWidth: "100%",
              }),
            }}
          />
        </div>
        {sortBy.field ? (
          <div className="no-drag">
            <SelectField
              className="w-40"
              autoFocus={!sortBy.direction}
              openMenuOnFocus={!sortBy.direction}
              value={sortByOptions.find((item) => item.value === sortBy.direction)}
              onChange={(value) => {
                onChange({
                  ...sortBy,
                  direction: value?.value || "ASC",
                });
              }}
              options={sortByOptions}
              placeholder={t("Condition")}
              dropdownIndicator={<ChevronDown className="w-3 h-3 text-gray-500" />}
              formatOptionLabel={(option) => {
                return (
                  <div className={"w-full flex items-center space-x-2"} key={option.value}>
                    <div>
                      {option.value === "ASC" ? (
                        <ArrowUpWideNarrow className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ArrowDownWideNarrow className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                    <div>{t(option.label)}</div>
                  </div>
                );
              }}
              stylesOverride={{
                control: (base) => ({
                  ...base,
                  minHeight: 30,
                  height: 30,
                  borderRadius: 8,
                  borderColor: "#d1d5dc",
                  boxShadow: "none",
                  ":hover": {
                    borderColor: "#d1d5db",
                  },
                  fontSize: 12,
                }),
                menu: (base) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                }),
              }}
            />
          </div>
        ) : null}
      </div>
      <div className="no-drag p-1 flex items-center rounded-md hover:bg-gray-100">
        <X
          className={"w-5 h-5 text-gray-400 cursor-pointer"}
          onClick={() => {
            onRemove(idx);
          }}
        />
      </div>
    </div>
  );
};

export default SortByBuilderItem;
